import React, { useState } from "react";
import { AppLayout, PageHeader } from "./_shared/AppLayout";
import { DEFAULT_CATEGORIES, getCategories } from "./_shared/categories";
import { useAppData } from "./_shared/store";
import ConfirmDialog from "./_shared/ConfirmDialog";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription,
} from "../../components/ui/dialog";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Plus, Trash2, Save, X } from "lucide-react";
import { toast } from "sonner";

/* ── Color palette for new categories ── */
const COLOR_OPTIONS = [
  { hex: "#ef4444", name: "Vermelho" },
  { hex: "#f97316", name: "Laranja" },
  { hex: "#f59e0b", name: "Âmbar" },
  { hex: "#eab308", name: "Amarelo" },
  { hex: "#84cc16", name: "Lima" },
  { hex: "#22c55e", name: "Verde" },
  { hex: "#14b8a6", name: "Teal" },
  { hex: "#06b6d4", name: "Ciano" },
  { hex: "#3b82f6", name: "Azul" },
  { hex: "#8b5cf6", name: "Violeta" },
  { hex: "#a855f7", name: "Roxo" },
  { hex: "#ec4899", name: "Pink" },
  { hex: "#f43f5e", name: "Rosa" },
  { hex: "#64748b", name: "Cinza" },
];

/* ── Modal for new category ── */
const NovaCategoriaModal = ({ open, onOpenChange, onSave }) => {
  const [nome, setNome] = useState("");
  const [desc, setDesc] = useState("");
  const [color, setColor] = useState(COLOR_OPTIONS[0].hex);

  const reset = () => { setNome(""); setDesc(""); setColor(COLOR_OPTIONS[0].hex); };

  const submit = (e) => {
    e?.preventDefault();
    if (!nome.trim()) return toast.error("Informe o nome da categoria.");
    const id = nome.trim().toLowerCase()
      .normalize("NFD").replace(/[\u0300-\u036f]/g, "")
      .replace(/[^a-z0-9]/g, "_");
    onSave({
      id,
      name: nome.trim(),
      color,
      bg: "bg-slate-500/20",
      text: "text-slate-300",
      desc: desc.trim() || `Categoria personalizada: ${nome.trim()}`,
      custom: true,
    });
    reset();
    onOpenChange(false);
    toast.success(`Categoria "${nome.trim()}" criada!`);
  };

  return (
    <Dialog open={open} onOpenChange={(v) => { if (!v) reset(); onOpenChange(v); }}>
      <DialogContent className="sm:max-w-md" data-testid="new-cat-modal">
        <DialogHeader>
          <DialogTitle className="font-display text-xl">Nova Categoria</DialogTitle>
          <DialogDescription>Crie uma categoria personalizada que aparecerá em todas as abas.</DialogDescription>
        </DialogHeader>
        <form onSubmit={submit} className="space-y-4">
          <div>
            <label className="text-xs font-semibold text-slate-400 uppercase tracking-wider">Nome</label>
            <Input
              value={nome}
              onChange={(e) => setNome(e.target.value)}
              placeholder="Ex: Pets, Saúde, Educação..."
              data-testid="cat-input-nome"
              autoFocus
            />
          </div>
          <div>
            <label className="text-xs font-semibold text-slate-400 uppercase tracking-wider">Descrição (opcional)</label>
            <Input
              value={desc}
              onChange={(e) => setDesc(e.target.value)}
              placeholder="Para que serve essa categoria?"
              data-testid="cat-input-desc"
            />
          </div>
          <div>
            <label className="text-xs font-semibold text-slate-400 uppercase tracking-wider mb-2 block">Cor</label>
            <div className="flex flex-wrap gap-2">
              {COLOR_OPTIONS.map((c) => (
                <button
                  key={c.hex}
                  type="button"
                  onClick={() => setColor(c.hex)}
                  className={`h-8 w-8 rounded-full border-2 transition-all ${
                    color === c.hex
                      ? "border-white scale-125 shadow-lg"
                      : "border-transparent hover:border-slate-500 hover:scale-110"
                  }`}
                  style={{ background: c.hex }}
                  title={c.name}
                  data-testid={`cat-color-${c.name}`}
                />
              ))}
            </div>
          </div>

          {/* Preview */}
          {nome.trim() && (
            <div className="pt-2 flex items-center gap-3">
              <span className="text-xs text-slate-500 uppercase">Preview:</span>
              <span
                className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full text-xs font-semibold"
                style={{
                  background: `${color}20`,
                  color: color,
                }}
              >
                <span className="h-2 w-2 rounded-full" style={{ background: color }} />
                {nome.trim()}
              </span>
            </div>
          )}

          <div className="flex gap-2 pt-2">
            <Button type="button" variant="outline" onClick={() => { reset(); onOpenChange(false); }} className="flex-1">
              <X className="h-4 w-4 mr-1.5" /> Cancelar
            </Button>
            <Button type="submit" className="flex-1 monyx-btn-primary text-white">
              <Save className="h-4 w-4 mr-1.5" /> Criar categoria
            </Button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
};

/* ── Main Page ── */
export default function Categorias() {
  const { data, addCustomCategory, removeCustomCategory } = useAppData();
  const customCats = data.customCategories || [];
  const allCats = getCategories(customCats);

  const [modalOpen, setModalOpen] = useState(false);
  const [confirmDel, setConfirmDel] = useState({ open: false, id: null, name: "" });

  const handleSave = (cat) => {
    if (allCats.find((c) => c.id === cat.id)) {
      toast.error(`Já existe uma categoria com esse nome.`);
      return;
    }
    addCustomCategory(cat);
  };

  const askRemove = (cat) => {
    setConfirmDel({ open: true, id: cat.id, name: cat.name });
  };

  const doRemove = () => {
    if (confirmDel.id) {
      removeCustomCategory(confirmDel.id);
      toast.success(`Categoria "${confirmDel.name}" removida`);
    }
  };

  return (
    <AppLayout>
      <PageHeader title="Categorias e Significados" subtitle="Como cada grupo é usado no seu painel" testid="categorias" />
      <div className="max-w-5xl mx-auto px-4 md:px-8 py-8">
        {/* Add button */}
        <div className="flex justify-end mb-4">
          <button
            onClick={() => setModalOpen(true)}
            className="monyx-btn-primary text-white rounded-full h-11 px-5 text-sm font-semibold inline-flex items-center justify-center gap-2"
            data-testid="add-categoria"
          >
            <Plus className="h-4 w-4" /> Nova categoria
          </button>
        </div>

        <div className="rounded-2xl bg-slate-900 border border-slate-800 overflow-hidden">
          <div className="hidden md:grid grid-cols-12 px-5 py-3 text-[11px] uppercase tracking-wider text-slate-400 font-semibold border-b border-slate-800">
            <div className="col-span-4">Categoria</div>
            <div className="col-span-7">Significado</div>
            <div className="col-span-1 text-right">Ações</div>
          </div>
          <div className="divide-y divide-slate-800">
            {allCats.map((c) => {
              const isCustom = !!c.custom;
              return (
                <div key={c.id} className="px-4 md:px-5 py-4 hover:bg-slate-900/60 transition flex flex-col md:grid md:grid-cols-12 md:items-center gap-2 md:gap-0" data-testid={`cat-${c.id}`}>
                  <div className="md:col-span-4">
                    <span
                      className={
                        isCustom
                          ? "inline-flex items-center gap-2 px-3 py-1.5 rounded-full text-xs font-semibold"
                          : `inline-flex items-center gap-2 px-3 py-1.5 rounded-full text-xs font-semibold ${c.bg} ${c.text}`
                      }
                      style={isCustom ? { background: `${c.color}20`, color: c.color } : undefined}
                    >
                      <span className="h-2 w-2 rounded-full" style={{ background: c.color }} />
                      {c.name}
                    </span>
                  </div>
                  <div className="md:col-span-7 text-sm text-slate-300 leading-relaxed">{c.desc}</div>
                  <div className="md:col-span-1 flex justify-end">
                    {isCustom && (
                      <button
                        onClick={() => askRemove(c)}
                        className="h-8 w-8 grid place-items-center rounded-lg hover:bg-slate-800 text-slate-400 hover:text-rose-400 transition"
                        title="Remover categoria"
                        data-testid={`cat-${c.id}-del`}
                      >
                        <Trash2 className="h-3.5 w-3.5" />
                      </button>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </div>

      <NovaCategoriaModal open={modalOpen} onOpenChange={setModalOpen} onSave={handleSave} />
      <ConfirmDialog
        open={confirmDel.open}
        onOpenChange={(v) => setConfirmDel((s) => ({ ...s, open: v }))}
        title="Remover categoria?"
        description={`Deseja remover a categoria "${confirmDel.name}"?`}
        onConfirm={doRemove}
      />
    </AppLayout>
  );
}
